import React, { useContext, useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import SmsIcon from "@mui/icons-material/Sms";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import "../Abdm.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { encryptTextApi, fetchAbdmAccessToken, formatAbhaId, useResendOTP, processAbhaProfileData } from "../../../../../helpers/abdm-helper";
import { abdmAPI } from "../../../../../services/abdm.api";
import Step1AadhaarInput from "./Step1AadhaarInput/Step1AadhaarInput";
import Step2OtpMobileVerification from "./Step2OtpMobileVerification/Step2OtpMobileVerification";
import Step3AbhaAddressSuggestions from "./Step3AbhaAddressSuggestions/Step3AbhaAddressSuggestions";
import Step4AbhaRegistered from "./Step4AbhaRegistered/Step4AbhaRegistered";

const AbhaCreateModal = ({ open, onClose, redirectUrl }) => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    const [userName, setUserName] = useState("");

    // Step Management
    const [step, setStep] = useState(1);

    // Form States
    const [aadhaar, setAadhaar] = useState("");
    const [consentChecked, setConsentChecked] = useState(false);
    const [otp, setOtp] = useState("");
    const [mobile, setMobile] = useState("");
    const [mobileOtp, setMobileOtp] = useState("");
    const [showMobileOtpStep, setShowMobileOtpStep] = useState(false);
    const [mobileOtpTxnId, setMobileOtpTxnId] = useState(null);

    // Token & Txn States
    const [abdmAccessToken, setAbdmAccessToken] = useState(null);
    const [txnId, setTxnId] = useState(null);
    const [otpMessage, setOtpMessage] = useState("");

    const [authToken, setAuthToken] = useState(null);
    const [encryptedAadhaar, setEncryptedAadhaar] = useState(null);
    const [encryptedMobileNumber, setEncryptedMobileNumber] = useState(null);

    // Profile States
    const [abhaSuggestions, setAbhaSuggestions] = useState([]);
    const [selectedAbhaAddress, setSelectedAbhaAddress] = useState("");
    const [abhaData, setAbhaData] = useState(null);
    const [isExistingAccount, setIsExistingAccount] = useState(false);

    useEffect(() => {
        const userData = localStorage.getItem("loggedinUserdetails") || localStorage.getItem("NIKSHAY_loggedinUserdetails");
        if (userData) {
            try {
                setUserName(JSON.parse(userData));
            } catch (error) {
                console.error("Error parsing user data:", error);
            }
        }
    }, []);

    // ==================== API LOGIC (RETAINED) ====================
    // Pure API call for Aadhaar OTP (for resend)
    const sendAadhaarOtpApi = async () => {
        const cleanAadhaar = aadhaar.replace(/-/g, "");

        if (!cleanAadhaar) {
            throw new Error("Please enter Aadhaar number");
        }

        // Check if this is the first call or a resend
        const isFirstCall = !encryptedAadhaar;

        // Use stored encrypted value for resend, or encrypt for first time
        let encrypted = encryptedAadhaar;
        if (!encrypted) {
            encrypted = await encryptTextApi(cleanAadhaar);
            setEncryptedAadhaar(encrypted); // Store for resend
        }

        const response = await abdmAPI.sendAadhaarOtp(
            { loginId: encrypted },
            {
                headers: {
                    accessToken: abdmAccessToken,
                },
            }
        );

        if (response?.success) {
            const responseData = response?.data;

            // ✅ CRITICAL: ALWAYS update txnId (including resends)
            if (responseData?.txnId) {
                setTxnId(responseData?.txnId);
            }

            setOtpMessage(responseData?.message || "OTP sent successfully");
            return responseData;
        } else {
            throw new Error(response?.message || "Failed to send OTP");
        }
    };

    // Pure API call for Mobile OTP (for resend)
    const sendMobileOtpApi = async (mobileNumber = null, currentTxnId = null) => {
        const mobileToUse = mobileNumber || mobile;
        const txnIdToUse = currentTxnId || txnId;

        if (!mobileToUse) {
            throw new Error("Please enter mobile number");
        }

        // Check if this is the first call or a resend
        const isFirstCall = !encryptedMobileNumber;

        // Use stored encrypted value for resend, or encrypt for first time
        let encrypted = encryptedMobileNumber;
        if (!encrypted) {
            encrypted = await encryptTextApi(mobileToUse);
            setEncryptedMobileNumber(encrypted); // Store for resend
        }

        const response = await abdmAPI.sendMobileUpdateOtp(
            {
                loginId: encrypted,
                txnId: txnIdToUse,
            },
            {
                headers: {
                    accessToken: abdmAccessToken,
                },
            }
        );

        if (response?.data) {
            // ONLY update mobileOtpTxnId on first call, not on resends
            if (isFirstCall && response?.data?.txnId) {
                setMobileOtpTxnId(response?.data?.txnId);
            }

            return response?.data;
        } else {
            throw new Error("Failed to send mobile OTP");
        }
    };

    // ==================== RESEND OTP HOOKS ====================

    const {
        countdown: aadhaarCountdown,
        resendCount: aadhaarResendCount,
        canResend: canResendAadhaar,
        formattedTime: aadhaarFormattedTime,
        handleResend: handleResendAadhaar,
        resetTimer: resetAadhaarTimer,
        startTimer: startAadhaarTimer,
        maxResendCount: aadhaarMaxResendCount
    } = useResendOTP({
        maxResendCount: 3,
        countdownSeconds: 60,
        autoStart: false,
        onResend: async () => {
            setIsLoading(true);
            try {
                const responseData = await sendAadhaarOtpApi();

                toast.success(responseData?.message || 'OTP resent successfully');
            } catch (error) {
                toast.error(error?.response?.data?.message || "Resend failed");
                throw error;
            } finally {
                setIsLoading(false);
            }
        }
    });

    const {
        countdown: mobileCountdown,
        resendCount: mobileResendCount,
        canResend: canResendMobile,
        formattedTime: mobileFormattedTime,
        handleResend: handleResendMobile,
        resetTimer: resetMobileTimer,
        startTimer: startMobileTimer,
        maxResendCount: mobileMaxResendCount
    } = useResendOTP({
        maxResendCount: 3,
        countdownSeconds: 60,
        autoStart: false,
        onResend: async () => {
            setIsLoading(true);
            try {
                await sendMobileOtpApi(mobile, txnId);

                toast.success("OTP resent successfully to mobile number");
            } catch (error) {
                toast.error(error?.response?.data?.message || "Resend failed");
                throw error;
            } finally {
                setIsLoading(false);
            }
        }
    });

    // ==================== EFFECTS & HANDLERS ====================

    useEffect(() => {
        if (open) loadAbdmToken();
        return () => {
            if (!open) setAbdmAccessToken(null);
        };
    }, [open]);

    const loadAbdmToken = async () => {
        try {
            const token = await fetchAbdmAccessToken(setIsLoading, toast);
            if (token) setAbdmAccessToken(token);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendAadhaarOtp = async () => {
        try {
            setIsLoading(true);

            // Use the pure API function
            const responseData = await sendAadhaarOtpApi();

            toast.success(responseData?.message || "OTP resent successfully");

            setStep(2);

            // Start Aadhaar timer
            resetAadhaarTimer();
            startAadhaarTimer();
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to send OTP");
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyAadhaarOtp = async () => {
        try {
            setIsLoading(true);

            const encryptedOtp = await encryptTextApi(otp);

            const response = await abdmAPI.verifyAadhaarOtp(
                {
                    txnId: txnId,
                    otpValue: encryptedOtp,
                    mobile: mobile
                },
                {
                    headers: {
                        accessToken: abdmAccessToken
                    }
                }
            );

            if (response?.success) {
                const profileData = response?.data?.ABHAProfile;
                const tokens = response?.data?.tokens;
                const isNew = response?.data?.isNew;
                const nextTxnId = response?.data?.txnId;

                // Store the token for later use
                setAuthToken(tokens?.token);
                setTxnId(nextTxnId);

                if (isNew === false) {
                    setIsExistingAccount(true);

                    // Compare mobile with trimmed strings
                    const userMobile = mobile?.toString().trim();
                    const profileMobile = profileData?.mobile?.toString().trim();

                    if (profileMobile && profileMobile === userMobile) {
                        const maskedAbhaNumber = formatAbhaId(profileData?.ABHANumber);

                        toast.warning(`ABHA Account Exist - We have found an ABHA ${maskedAbhaNumber} against the Aadhaar Number provided by you.`)

                        handleVerifyAbhaUser(tokens?.token);

                    } else {
                        setShowMobileOtpStep(true);
                        await handleSendMobileOtp(mobile, nextTxnId);
                    }
                } else {
                    // NEW ACCOUNT (isNew = true) - Need to create ABHA address
                    setIsExistingAccount(false);

                    if (profileData?.mobile) {
                        // Scenario 2.1.2: Mobile matches + new account
                        const userMobile = mobile?.toString().trim();
                        const profileMobile = profileData?.mobile?.toString().trim();

                        if (profileMobile === userMobile) {
                            toast.success("Mobile number for communication is same as Aadhaar number. No additional information required.")
                            // Move to Step 3 for ABHA address creation
                            setStep(3);
                        } else {
                            // Mobile values don't match, need verification
                            setShowMobileOtpStep(true);
                            await handleSendMobileOtp(mobile, response?.data?.data?.txnId);
                        }
                    } else {
                        // Scenario 2.2.2: Mobile is null + new account
                        setShowMobileOtpStep(true);
                        await handleSendMobileOtp(mobile, response?.data?.data?.txnId);
                    }
                }
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to verify OTP")
        }
        finally {
            setIsLoading(false);
        }
    };

    const handleSendMobileOtp = async (mobileNumber, currentTxnId) => {
        try {
            setIsLoading(true);
            await sendMobileOtpApi(mobileNumber, currentTxnId);

            toast.success("OTP sent to mobile number")

            // Start mobile timer
            resetMobileTimer();
            startMobileTimer();
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to send mobile OTP")
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyMobileOtp = async () => {
        try {
            setIsLoading(true);

            const encryptedMobileOtp = await encryptTextApi(mobileOtp);

            const response = await abdmAPI.verifyMobileUpdateOtp(
                {
                    txnId: mobileOtpTxnId,
                    otpValue: encryptedMobileOtp
                },
                {
                    headers: {
                        accessToken: abdmAccessToken
                    }
                }
            );

            if (response?.data) {
                if (isExistingAccount) {
                    toast.success("Mobile number verified successfully")

                    await handleVerifyAbhaUser();
                } else {
                    toast.success("Mobile number verified successfully")
                    setStep(3);
                }
            } else {
                throw new Error(response?.data?.message || "Failed to verify mobile OTP");
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to verify mobile OTP")
        } finally {
            setIsLoading(false);
        }
    };

    // Step 3: Fetch ABHA Address Suggestions
    const fetchAbhaSuggestions = async () => {
        try {
            setIsLoading(true);
            const response = await abdmAPI.abhaEnrollSuggestions(
                { txnId: txnId },
                {
                    headers: {
                        accessToken: abdmAccessToken,
                    },

                }
            );

            if (response) {
                const suggestions = response?.abhaAddressList;
                setAbhaSuggestions(suggestions);
            }
        } catch (error) {
            toast.error(error?.response?.message || "Failed to fetch ABHA suggestions")
        } finally {
            setIsLoading(false);
        }
    };

    // Step 4: Submit Preferred ABHA Address
    const handleSubmitAbhaAddress = async () => {
        try {
            setIsLoading(true);

            const response = await abdmAPI.preferredAbhaAddress(
                {
                    txnId: txnId,
                    abhaAddress: selectedAbhaAddress,
                },
                {
                    headers: {
                        accessToken: abdmAccessToken,
                    },
                }
            );
            // console.log("Preferred ABHA Address Response:", response);

            if (response) {
                // Now verify with the auth token
                await handleVerifyAbhaUser();
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to set ABHA address")
            setIsLoading(false);
        }
    };

    // Step 4: Verify ABHA User and Get Profile
    const handleVerifyAbhaUser = async (tokenOverride = null) => {
        // Use tokenOverride if provided, otherwise use authToken from state
        const tokenToUse = tokenOverride || authToken;
        try {
            const response = await abdmAPI.verifyAbhaOtpUser(
                {
                    token: tokenToUse,
                },
                {
                    headers: {
                        accessToken: abdmAccessToken,
                    },
                }
            );

            if (response?.success) {
                const profileData = response?.data;
                setAbhaData(profileData);

                // Process and store ABHA profile data
                processAbhaProfileData(profileData);

                // Check if this is an existing account
                if (isExistingAccount) {
                    // Show "ABHA Account Exist" alert for existing accounts
                    const maskedAbhaNumber = formatAbhaId(profileData?.ABHANumber);

                    toast.success(`ABHA Account Exist - We have found an ABHA ${maskedAbhaNumber} against the Aadhaar Number provided by you.`)

                    setStep(4);
                } else {
                    // New account - directly move to Step 4
                    setStep(4);
                }
            } else {
                throw new Error(response?.data?.message || "Failed to verify ABHA user");
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to verify ABHA user")
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegisterBeneficiary = () => {
        handleClose();
        if (redirectUrl) {
            navigate(redirectUrl);
        } else {
            navigate("/detailsabha");
        }
    };

    //   const handleRegisterBeneficiary = () => {
    //     // if (abhaData) setAbhaProfile(abhaData);
    //     handleClose();
    //     // navigate(`${paths?.user?.commonPoint}${paths?.user?.patientRegistration}`);
    //   };

    // Handle Close
    const handleClose = () => {
        resetModal();
        onClose();
    };

    // Reset Modal
    const resetModal = () => {
        setStep(1);
        setAadhaar("");
        setConsentChecked(false);
        setOtp("");
        setMobile("");
        setMobileOtp("");
        setShowMobileOtpStep(false);
        setMobileOtpTxnId(null);
        setTxnId(null);
        setOtpMessage("");
        setAuthToken(null);
        setAbhaSuggestions([]);
        setSelectedAbhaAddress("");
        setAbhaData(null);
        setAbdmAccessToken(null);
        setIsExistingAccount(false);
        setEncryptedAadhaar(null);
        setEncryptedMobileNumber(null);
        resetAadhaarTimer();
        resetMobileTimer();
    };

    // ==================== RENDER ====================

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <Step1AadhaarInput
                        aadhaar={aadhaar}
                        setAadhaar={setAadhaar}
                        consentChecked={consentChecked}
                        setConsentChecked={setConsentChecked}
                        userName={userName?.firstname || ""}
                        onNext={handleSendAadhaarOtp}
                        isLoading={isLoading}
                    />
                );
            case 2:
                return (
                    <Step2OtpMobileVerification
                        otp={otp}
                        setOtp={setOtp}
                        mobile={mobile}
                        setMobile={setMobile}
                        mobileOtp={mobileOtp}
                        setMobileOtp={setMobileOtp}
                        showMobileOtpStep={showMobileOtpStep}
                        onVerifyAadhaarOtp={handleVerifyAadhaarOtp}
                        onVerifyMobileOtp={handleVerifyMobileOtp}
                        onResendAadhaarOtp={handleResendAadhaar}
                        onResendMobileOtp={handleResendMobile}
                        // Aadhaar OTP Timer Props
                        aadhaarCanResend={canResendAadhaar}
                        aadhaarFormattedTime={aadhaarFormattedTime}
                        aadhaarResendCount={aadhaarResendCount}
                        aadhaarMaxResendCount={aadhaarMaxResendCount}
                        // Mobile OTP Timer Props
                        mobileCanResend={canResendMobile}
                        mobileFormattedTime={mobileFormattedTime}
                        mobileResendCount={mobileResendCount}
                        mobileMaxResendCount={mobileMaxResendCount}
                        isLoading={isLoading}
                    />
                );
            case 3:
                return (
                    <Step3AbhaAddressSuggestions
                        abhaSuggestions={abhaSuggestions}
                        selectedAbhaAddress={selectedAbhaAddress}
                        setSelectedAbhaAddress={setSelectedAbhaAddress}
                        onFetchSuggestions={fetchAbhaSuggestions}
                        onSubmit={handleSubmitAbhaAddress}
                        onBack={() => setStep(2)}
                        isLoading={isLoading}
                    />
                );
            case 4:
                return (
                    <Step4AbhaRegistered
                        abhaData={abhaData}
                        onRegisterBeneficiary={handleRegisterBeneficiary}
                        isLoading={isLoading}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: "25px", p: 1 } }}
        >
            <DialogTitle sx={{ px: 4, pt: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6" fontWeight={700}>
                            {step === 4 ? "Register New Beneficiary" : "Create ABHA"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Create your Ayushman Bharat Health Account (ABHA)
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} className="close-abhabtn">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ px: 4, pb: 4 }}>
                <Divider sx={{ mb: 3 }} />

                {/* Progress Stepper UI */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4, position: 'relative' }}>
                    <Box sx={{ position: 'absolute', top: '18px', left: '10%', right: '10%', height: '2px', bgcolor: '#eee', zIndex: 0 }} />
                    {[
                        { icon: <VerifiedUserIcon />, label: "Aadhaar", s: 1 },
                        { icon: <SmsIcon />, label: "Verify", s: 2 },
                        { icon: <FingerprintIcon />, label: "Address", s: 3 },
                        { icon: <CheckCircleIcon />, label: "Complete", s: 4 }
                    ].map((item) => (
                        <Box key={item.s} sx={{ zIndex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '25%' }}>
                            <Box sx={{
                                width: 36, height: 36, borderRadius: '50%', bgcolor: step >= item.s ? '#2b57fb' : '#fff',
                                color: step >= item.s ? '#fff' : '#ccc', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', border: '2px solid', borderColor: step >= item.s ? '#2b57fb' : '#eee',
                                mb: 1, transition: '0.3s'
                            }}>
                                {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: step >= item.s ? 600 : 400, color: step >= item.s ? '#2b57fb' : '#999' }}>
                                {item.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Step Content */}
                <Box sx={{ mt: 2 }}>
                    {renderStep()}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default AbhaCreateModal;